import { TourConfig, startTour, CONTINUE_PLATFORM_TOUR_BUTTON } from './TourSystem';
import PageWelcomeModal from './PageWelcomeModal';
import 'driver.js/dist/driver.css';

const academyTourConfig: TourConfig = {
    tourId: 'academy',
    steps: [
        {
            popover: {
                title: '🎓 Welcome to TechUnity Academy',
                description: 'Your complete professional development platform. This tour will show you every feature available for learning new skills, earning certifications, and advancing your career. Let me walk you through everything step by step.',
            }
        },
        {
            element: '#academy-nav-wrapper',
            popover: {
                title: '🧭 Learning Mode Selector',
                description: 'This is your main navigation control. It allows you to switch between two different learning experiences: browsing individual courses OR following structured career paths. Both modes have completely different features designed for different goals.',
                side: "bottom",
            }
        },
        {
            element: '#academy-tab-courses',
            popover: {
                title: '📚 Browse Courses Mode',
                description: 'Click this tab to access our entire course library. In this mode, you can search through thousands of courses, filter by category (technology, business, design, etc.), and browse by topic. Best for learning specific skills or exploring new subjects.',
                side: "bottom",
            }
        },
        {
            element: '#academy-tab-careers',
            popover: {
                title: '🚀 Career Paths Mode (Current View)',
                description: 'You are currently viewing Career Paths mode. This structured approach shows you complete job roles (like "Senior Developer" or "Data Analyst") with curated course sequences to achieve each role. Perfect if you want to switch careers or advance to a specific position.',
                side: "bottom",
            }
        },
        {
            element: '#academy-paths',
            popover: {
                title: '🏢 Department Selection Grid',
                description: 'These cards represent different professional sectors. Each department contains multiple career roles. For example, the "Software Development" department has roles like Full Stack Developer, Mobile Developer, DevOps Engineer, etc. Click any department to explore its career opportunities with salary ranges and required skills.',
                side: "top",
            }
        },
        {
            popover: {
                title: '💼 How Career Paths Work',
                description: 'When you click a department card, you will see a list of specific job titles within that field. Each job shows: average salary data, required experience level (entry/mid/senior), core skills needed, and a curated learning path. Click any job to see the exact courses that will prepare you for that role.',
            }
        },
        {
            popover: {
                title: '📖 Now Let Me Show You Browse Courses Mode',
                description: 'I am going to switch you to the Browse Courses view so you can see the search, filters, and course catalog in action. This will happen automatically in 3 seconds...',
            }
        }
    ],
    onComplete: () => {
        // After the first 7 steps, switch to courses mode and continue
        setTimeout(() => {
            const coursesTab = document.getElementById('academy-tab-courses');
            if (coursesTab) {
                coursesTab.click();
                setTimeout(() => {
                    startCoursesModeTour();
                }, 500);
            }
        }, 1000);
    }
};

const coursesModeTourConfig: TourConfig = {
    tourId: 'academy-courses-mode',
    steps: [
        {
            element: '#academy-search',
            popover: {
                title: '🔍 Intelligent Course Search',
                description: 'This is a real-time search engine for our course catalog. Type keywords like "Python", "Marketing", "Excel", or "Machine Learning" and watch the results update instantly as you type. You can search by: technology names, topics, instructor names, course titles, or skill levels. Try it now!',
                side: "bottom",
            }
        },
        {
            element: '#academy-categories',
            popover: {
                title: '🏷️ Industry Category Filters',
                description: 'These category buttons let you narrow courses by professional sector. Available categories include: Development (programming), Business (MBA-level), Design (creative), Marketing, Data Science, Health & Fitness, Law, Engineering, Environment, and Teaching. Click any category to filter the course grid below. You can combine category filtering with the search bar for precise results.',
                side: "top",
            }
        },
        {
            element: '#academy-courses',
            popover: {
                title: '📦 Course Catalog Grid',
                description: 'This grid displays all courses matching your current search and category filters. Each course card shows: preview image, course title, instructorinstructor name, category badge (top-left corner), platform provider badge (top-right - Udemy, Coursera, etc.), estimated duration (bottom-left), and star rating (bottom-right). Click any course card to open the full course page where you can enroll.',
                side: "top",
            }
        },
        {
            popover: {
                title: '⭐ Course Quality & Features',
                description: 'Every course in our catalog meets strict quality standards: minimum 4-star rating from verified students, professional instructors with proven track records, completion certificates, clear learning outcomes, and accurate time estimates. We partner with top platforms like Udemy, Coursera, edX, and LinkedIn Learning.',
            }
        },
        {
            popover: {
                title: '📈 Progress Tracking & Certificates',
                description: 'As you complete courses, your profile automatically updates with earned certificates and skill badges. These credentials appear on your public TechUnity profile, making you more visible to employers browsing the Talent Network. Completed courses also unlock higher-paying projects on the Dispatch board.',
            }
        },
        {
            popover: {
                title: '💡 Pro Tips for Success',
                description: '(1) Use Career Paths if you have a specific job goal - it shows you exactly what to learn. (2) Use Browse Courses to fill knowledge gaps or explore new topics. (3) Combine learning with real projects from Dispatch to build your portfolio. (4) Complete courses fully to earn certificates that boost your profile visibility. (5) Category filters + search = fastest way to find what you need.',
            }
        },
        {
            popover: {
                title: '🎯 You Are Ready to Learn',
                description: `You now understand both learning modes in Academy. Switch between "Career Paths" for structured roadmaps and "Browse Courses" for individual skills. Every course you complete increases your earning potential and project opportunities on TechUnity.${CONTINUE_PLATFORM_TOUR_BUTTON}`,
            }
        }
    ],
};

export const startAcademyTour = () => {
    startTour(academyTourConfig);
};

const startCoursesModeTour = () => {
    startTour(coursesModeTourConfig);
};

const AcademyTour = () => {
    return (
        <PageWelcomeModal
            title="Welcome to Academy"
            description="Your complete learning platform with courses, certifications, and career paths."
            onStartTour={startAcademyTour}
            tourId="academy"
        />
    );
};

export default AcademyTour;
